/**
 * MDF eligible / ineligible activity-type reference lists.
 *
 * Source: the authoritative MDF Cash & Credit "Eligible vs. Ineligible
 * activity types" wiki list. Displayed (read-only) on the Claims tab so a
 * reviewer can compare an invoice's line items against the SOP's activity
 * rules while judging eligibility.
 *
 * This is REFERENCE content, not a live classifier. The agent's Step 6 flags
 * ineligible line items (travel / other-ineligible / venue-ambiguous /
 * undetermined) and those are shown separately on the Claims tab; this list is
 * the human-readable rubric behind those calls. Keep it in sync with the SOP
 * (sop/mdf_sop.yaml ineligible_line_items) and the source wiki if the activity
 * rules change.
 */

export const MDF_ELIGIBLE_ACTIVITIES = [
  { name: 'End Customer Events', note: 'In-person & virtual — showcase an AWS solution to end customers (networking, roundtables, partner-hosted, etc.)' },
  { name: 'Customer-Focused Webinars', note: 'Partner-led on their AWS solution, or joint with other vendors' },
  { name: 'Display Advertising & Search Marketing', note: 'Display, paid search, SEO, social media' },
  { name: 'Email Campaign', note: 'Templates, design, execution, web copy, landing pages, microsites' },
  { name: 'Industry Conference Event', note: 'Participation in a 3rd-party industry/tech conference to showcase AWS solutions' },
  { name: 'Lead List Purchase', note: 'List purchase, enhancement / enrichment' },
  { name: 'Customer Event with an AWS-led Event', note: 'Networking alongside re:Invent/Summits IF sponsoring that AWS event and complying with sponsorship rules' },
  { name: 'Partner Produced Case Study', note: 'Written and/or video, on a completed deployment showcasing an AWS solution' },
  { name: 'Partner Sales Content', note: 'Whitepaper, e-book, solution/technical brief, landing pages' },
  { name: 'Partner Sales Kick Off (SKO)', note: 'Partner event on annual sales strategy + GTM with AWS' },
  { name: 'Telemarketing Campaigns', note: 'Call campaigns + assets (battlecard, call script, first-call deck)' },
  { name: 'Swag / Gifts / Giveaways', note: 'Logo items specifically tied to an MDF activity / AWS campaign' },
  { name: 'AWS Led Joint Campaign', note: 'Invite only' },
  { name: 'Video Conferencing', note: 'Zoom / GoToMeeting / WebEx hosting for qualifying events' },
  { name: 'Catering', note: 'Food, beverages AND gratuities for a qualifying event; food vouchers for virtual events. Gratuity on catering IS eligible.' },
  { name: 'Social / Recreational Events', note: 'For training/marketing events showcasing AWS solutions. Ineligible for WWPS MDF wallets.' },
];

export const MDF_INELIGIBLE_ACTIVITIES = [
  { name: 'Travel & Accommodation', note: 'Flights, hotels, individual meals, taxis, rideshares, buses — for partner, AWS staff, AND 3rd-party vendors. (Enforced as MDFTravel.)' },
  { name: 'Relationship Events', note: 'Networking-only / not focused on the partner AWS solution; morale-building' },
  { name: 'AWS Staff Activity', note: 'Gifts / entertainment / business costs for AWS employees' },
  { name: 'Headcount', note: 'Staff / wages unless directly tied to the activity; no recruiting events' },
  { name: 'Charity Donations', note: '' },
  { name: 'AWS Sponsored Events', note: 'Offsetting fees for re:Invent/Summits (sponsorship, tickets, meeting rooms)' },
  { name: 'Incentives', note: "Can't incent customers (e.g. $500 for a reference)" },
  { name: 'Cancellation Fees', note: 'Deposits / incurred costs if the activity is cancelled' },
  { name: 'Internal Costs', note: 'Only 3rd-party agency costs are paid; no internal hours, in-house creative, FTE/temp/contract labor' },
  { name: 'AWS Partner Business Costs', note: 'Normal opex, overhead, capex' },
  { name: 'Amazon.com Merchandise', note: 'Gift cards, Echos, Kindles, etc.' },
  { name: 'Alcohol', note: 'Ineligible — a line item matching "alcohol" is rejected (MDFIneligible)' },
  { name: 'Rush Fees', note: 'Expedited / rush surcharges are not reimbursable' },
  { name: 'SPIFs', note: '' },
];
